/**
 * Hotel Config Slice
 *
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CurrencyConfigData } from '../api/currencyConfigApi';
import { fetchCurrencyConfigThunk } from '../thunks/currencyConfigThunk';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';
import { TEMPLATE_TYPES } from '@/features/chat/constants/templateTypes';

// ============================================
// Types
// ============================================

/**
 * Search context from listing API response (searchContext field).
 * Contains search criteria like destination, dates, room config etc.
 */
export type ListingSearchContext = Record<string, unknown>;

export interface HotelConfigState {
  /** Search context from listing API response - used for listing page and initial details fetch */
  readonly listingSearchContext: ListingSearchContext | null;
  /** Currency/region/language config (fetched once, reused everywhere) */
  readonly currencyConfig: CurrencyConfigData | null;
  readonly isCurrencyConfigLoading: boolean;
  readonly currencyConfigError: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialHotelConfigState: HotelConfigState = {
  listingSearchContext: null,
  currencyConfig: null,
  isCurrencyConfigLoading: false,
  currencyConfigError: null,
};

// ============================================
// Slice
// ============================================

const hotelConfigSlice = createSlice({
  name: 'hotels/config',
  initialState: initialHotelConfigState,
  reducers: {
    /**
     * Set search context from listing API response
     * Used for listing page operations and initial details page fetch
     */
    setListingSearchContext: (state, action: PayloadAction<ListingSearchContext | null>) => {
      state.listingSearchContext = action.payload;
    },

    /**
     * Clear hotel config (when navigating away from hotels flow)
     */
    clearHotelConfig: (state) => {
      state.listingSearchContext = null;
      state.currencyConfig = null;
      state.currencyConfigError = null;
    },

    /**
     * Reset hotel config state
     */
    resetHotelConfigState: () => initialHotelConfigState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrencyConfigThunk.pending, (state) => {
        state.isCurrencyConfigLoading = true;
        state.currencyConfigError = null;
      })
      .addCase(fetchCurrencyConfigThunk.fulfilled, (state, action) => {
        state.isCurrencyConfigLoading = false;
        state.currencyConfig = action.payload;
      })
      .addCase(fetchCurrencyConfigThunk.rejected, (state, action) => {
        state.isCurrencyConfigLoading = false;
        state.currencyConfigError = action.payload ?? 'Failed to fetch currency config';
      });

    builder.addCase(setActiveTemplate, (state, action) => {
      if (
        action.payload.template === TEMPLATE_TYPES.HOTEL_DETAILS &&
        action.payload.data &&
        !state.listingSearchContext
      ) {
        const sc = (action.payload.data as Record<string, unknown>)['searchContext'] as
          | Record<string, unknown>
          | undefined;
        if (sc) state.listingSearchContext = sc;
      }
    });

    builder.addCase(updateTemplateFromStream, (state, action) => {
      if (
        action.payload.activeTemplate === TEMPLATE_TYPES.HOTEL_DETAILS &&
        action.payload.templateData &&
        !state.listingSearchContext
      ) {
        const sc = (action.payload.templateData as Record<string, unknown>)['searchContext'] as
          | Record<string, unknown>
          | undefined;
        if (sc) state.listingSearchContext = sc;
      }
    });
  },
});

// ============================================
// Actions
// ============================================

export const { setListingSearchContext, clearHotelConfig, resetHotelConfigState } =
  hotelConfigSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const hotelConfigReducer = hotelConfigSlice.reducer;
